import React from 'react';
import * as Sentry from '@sentry/react';
import { normalizeCaughtError } from '@/lib/sentry';
import { Button } from '@/components/ui/button';
import { AlertCircle } from 'lucide-react';

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
  eventId?: string;
}

/**
 * Error Boundary component that catches React component errors
 * and reports them to Sentry with user-friendly fallback UI
 */
class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: unknown): ErrorBoundaryState {
    return { hasError: true, error: normalizeCaughtError(error) };
  }

  componentDidCatch(error: unknown, errorInfo: React.ErrorInfo) {
    // getDerivedStateFromError normalized this throw during the render phase
    // and React commits that state before calling componentDidCatch — reuse
    // the object so one wrapper serves state, report, and dialog. The ?? arm
    // is a dead-in-practice guard against atypical React internals only.
    const normalized = this.state.error ?? normalizeCaughtError(error);
    const eventId = Sentry.captureException(normalized, {
      contexts: {
        react: {
          componentStack: errorInfo.componentStack,
        },
      },
      // Keep the raw thrown value: for non-Error throws the wrapped message
      // may be lossy, and the payload's shape identifies the throw site.
      extra: { originalError: error },
    });
    // Stored so the report dialog attaches to THIS event (the one carrying
    // componentStack + originalError) instead of capturing a bare duplicate.
    this.setState({ eventId });
  }

  handleReportIssue = () => {
    const eventId =
      this.state.eventId ?? Sentry.captureException(this.state.error);
    Sentry.showReportDialog({
      eventId,
      title: 'حدث خطأ ما',
      subtitle: 'يرجى وصف المشكلة التي واجهتك',
      subtitle2: '',
      labelName: 'الاسم',
      labelEmail: 'البريد الإلكتروني',
      labelComments: 'ماذا حدث؟',
      labelClose: 'إغلاق',
      labelSubmit: 'إرسال',
      errorGeneric:
        'حدث خطأ غير معروف أثناء إرسال تقريرك. يرجى المحاولة مرة أخرى.',
      errorFormEntry: 'بعض الحقول غير صالحة. يرجى تصحيح الأخطاء والمحاولة مرة أخرى.',
      successMessage: 'تم إرسال ملاحظاتك. شكرًا!',
    });
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
          <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
            <div className="flex justify-center mb-4">
              <AlertCircle className="h-16 w-16 text-red-500" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              حدث خطأ ما
            </h1>
            <p className="text-gray-600 mb-6">
              عذرًا، حدث خطأ غير متوقع. يرجى المحاولة مرة أخرى أو الاتصال بالدعم
              الفني.
            </p>
            <div className="space-y-3">
              <Button onClick={this.handleReload} className="w-full">
                إعادة تحميل الصفحة
              </Button>
              <Button
                onClick={this.handleReportIssue}
                variant="outline"
                className="w-full"
              >
                إبلاغ عن المشكلة
              </Button>
            </div>
            {import.meta.env.MODE === 'development' && this.state.error && (
              <div className="mt-6 text-start">
                <p className="text-sm font-mono text-red-600 bg-red-50 p-4 rounded overflow-auto">
                  {this.state.error.toString()}
                </p>
              </div>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
